import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Brand, Field } from "../components/common";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AuthLayout = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between bg-[#0B192C] p-12 text-white">
      <Brand light />
      <div>
        <h2 className="font-heading text-4xl font-bold leading-tight">Sell trusted deals.<br /><span className="text-emerald-400">Earn clear commissions.</span></h2>
        <ul className="mt-8 space-y-4 text-slate-300">
          <li className="flex gap-3"><BadgeCheck className="h-5 w-5 text-emerald-400" /> Fixed prices set by CommissionWorld — same for every customer</li>
          <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-emerald-400" /> Every order is permanently linked to your Sales ID</li>
          <li className="flex gap-3"><Wallet className="h-5 w-5 text-emerald-400" /> Track pending, approved and paid commissions in one place</li>
        </ul>
      </div>
      <p className="text-xs text-slate-500">© {new Date().getFullYear()} CommissionWorld · <Link to="/privacy" className="underline">Privacy notice</Link></p>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8"><Brand /></div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const ErrorBox = ({ msg }) => msg ? <div data-testid="form-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div> : null;

export function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav(user.role === "admin" ? "/admin" : "/app", { replace: true }); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/login", f); setUser(data); }
    catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <AuthLayout title="Welcome back" subtitle="Log in to your CommissionWorld account">
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox msg={err} />
        <Field label="Email"><Input data-testid="login-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12" /></Field>
        <Field label="Password"><Input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12" /></Field>
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-emerald-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Button data-testid="login-submit-button" disabled={busy} className="h-12 w-full text-base">{busy ? "Logging in…" : "Log in"}</Button>
        <p className="text-center text-sm text-slate-600">New Sales Executive? <Link to="/register" className="font-semibold text-emerald-700 hover:underline" data-testid="register-link">Register free</Link></p>
      </form>
    </AuthLayout>
  );
}

export function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", password: "", confirm: "" });
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm) return setErr("Passwords do not match");
    if (!agree) return setErr("Please accept the privacy notice to continue");
    setBusy(true);
    try {
      const { confirm, ...body } = f;
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      toast.success(`Welcome! Your Sales ID is ${data.sales_id}`);
      nav("/app");
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <AuthLayout title="Become a Sales Executive" subtitle="Free registration. You will receive a permanent Sales ID instantly.">
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox msg={err} />
        <Field label="Full name" required><Input data-testid="register-name-input" required value={f.name} onChange={set("name")} className="h-12" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Phone number" required><Input data-testid="register-phone-input" required placeholder="03XX-XXXXXXX" value={f.phone} onChange={set("phone")} className="h-12" /></Field>
          <Field label="City"><Input data-testid="register-city-input" value={f.city} onChange={set("city")} className="h-12" /></Field>
        </div>
        <Field label="Email" required><Input data-testid="register-email-input" type="email" required value={f.email} onChange={set("email")} className="h-12" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Password" required hint="At least 8 characters"><Input data-testid="register-password-input" type="password" required minLength={8} value={f.password} onChange={set("password")} className="h-12" /></Field>
          <Field label="Confirm password" required><Input data-testid="register-confirm-input" type="password" required value={f.confirm} onChange={set("confirm")} className="h-12" /></Field>
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input type="checkbox" data-testid="register-privacy-checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4 accent-emerald-600" />
          I have read the <Link to="/privacy" target="_blank" className="text-emerald-700 underline">privacy notice</Link> and agree to only book orders with customer consent.
        </label>
        <Button data-testid="register-submit-button" disabled={busy} className="h-12 w-full text-base">{busy ? "Creating account…" : "Create my account"}</Button>
        <p className="text-center text-sm text-slate-600">Already registered? <Link to="/login" className="font-semibold text-emerald-700 hover:underline">Log in</Link></p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setMsg(data.message); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthLayout title="Reset your password" subtitle="Enter the email you registered with.">
      {msg ? (
        <div data-testid="forgot-success" className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">{msg}</div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBox msg={err} />
          <Field label="Email"><Input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" /></Field>
          <Button data-testid="forgot-submit-button" className="h-12 w-full">Request reset link</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-emerald-700 hover:underline">Back to login</Link></p>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }); toast.success("Password updated. Please log in."); nav("/login"); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox msg={err} />
        <Field label="New password" hint="At least 8 characters"><Input data-testid="reset-password-input" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className="h-12" /></Field>
        <Button data-testid="reset-submit-button" className="h-12 w-full">Update password</Button>
      </form>
    </AuthLayout>
  );
}

export function Privacy() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/public/settings").then((r) => setS(r.data)); }, []);
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-2xl rounded-xl border bg-white p-8">
        <Brand />
        <h1 className="mt-6 font-heading text-3xl font-bold">Privacy notice</h1>
        <p className="mt-4 whitespace-pre-line text-slate-700" data-testid="privacy-text">{s?.privacy_notice}</p>
        {(s?.support_email || s?.support_phone) && <p className="mt-6 text-sm text-slate-500">Contact: {s.support_email} {s.support_phone}</p>}
        <Link to="/" className="mt-8 inline-block text-emerald-700 hover:underline">Back to CommissionWorld</Link>
      </div>
    </div>
  );
}
